import React, { useEffect } from 'react'
import { useKeyboardControls } from '../../../contexts/KeyboardControlsContext'

interface KeyboardControlProps {
  children: React.ReactElement

  /**
   * Specifies if the element is externally controlled, such as an iframe or Unity component.
   *
   * @default false
   */
  isExternal?: boolean
  /**
   * If true, the element is responsible for giving away focus when appropriate.
   *
   * @default false
   */
  manualFocusHandling?: boolean

  onFocusEnterFromStart?: (() => void) | null
  onFocusEnterFromEnd?: (() => void) | null
};

/**
 * A wrapper component that adds keyboard control functionality to the provided child element.
 *
 * Requires the `KeyboardControlContext` to be provided by `KeyboardControlsProvider`.
 *
 * @param {boolean} [props.isExternal=false] - Whether the child element is externally managed (e.g., an iframe or Unity content).
 * @param {boolean} [props.manualFocusHandling=false] - If true, the element is responsible for giving away focus when appropriate.
 */
const KeyboardControl: React.FC<KeyboardControlProps> = ({ children, isExternal = false, manualFocusHandling = false, onFocusEnterFromStart = null, onFocusEnterFromEnd = null }: KeyboardControlProps) => {
  const keyboardControls = useKeyboardControls()

  const extendedChild = React.cloneElement(children, {
    onFocus: keyboardControls.handleNonKeyboardFocus
  })

  const id = extendedChild.props.id

  useEffect(() => {
    keyboardControls.addFocusableElement(id, isExternal, manualFocusHandling, onFocusEnterFromStart, onFocusEnterFromEnd)

    return () => {
      keyboardControls.removeFocusableElement(id)
    }
  }, [keyboardControls, id, isExternal, manualFocusHandling, onFocusEnterFromStart, onFocusEnterFromEnd])

  return (
    extendedChild
  )
}

export default KeyboardControl
